import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../config/api';

export default function ForgotPassword() {
  const [step, setStep] = useState<'email' | 'code' | 'newPassword'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/auth/forgot-password', { email });
      if (response.data.success) {
        setStep('code');
        setSuccess('Si el correo existe, recibirás un código de verificación. Revisa también las carpetas de spam y promociones.');
      } else {
        setError(response.data.error || 'Error al enviar código');
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError('Demasiadas solicitudes. Espera unos minutos e intenta de nuevo.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('No se pudo enviar el código. Verifica tu conexión a internet e intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (step === 'code') {
      setStep('newPassword');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/auth/reset-password', {
        email,
        code,
        newPassword,
      });
      if (response.data.success) {
        setSuccess('Contraseña actualizada correctamente. Redirigiendo...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        const errorMessages: Record<string, string> = {
          'INVALID_MFA_CODE': 'Código inválido',
          'MFA_CODE_EXPIRED': 'El código ha expirado. Solicita uno nuevo.',
          'INVALID_PASSWORD': 'La contraseña no cumple con los requisitos de seguridad',
          'USER_NOT_FOUND': 'Usuario no encontrado',
        };
        setError(errorMessages[response.data.error] || 'Error al cambiar contraseña');
      }
    } catch (err: any) {
      const errorCode = err.response?.data?.error;
      const errorMessages: Record<string, string> = {
        'INVALID_MFA_CODE': 'Código inválido',
        'MFA_CODE_EXPIRED': 'El código ha expirado. Solicita uno nuevo.',
        'INVALID_PASSWORD': 'La contraseña no cumple con los requisitos de seguridad',
      };
      setError(errorMessages[errorCode] || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src="/logo.jpeg" alt="Fuigo" className="mx-auto h-24 w-auto mb-4" />
          <h2 className="text-3xl font-extrabold text-gray-900">Recuperar contraseña</h2>
          <p className="mt-2 text-sm text-gray-600">
            {step === 'email' && 'Ingresa tu correo para recibir un código de verificación'}
            {step === 'code' && 'Ingresa el código que enviamos a tu correo'}
            {step === 'newPassword' && 'Ingresa tu nueva contraseña'}
          </p>
        </div>

        {error && <div className="rounded-md bg-red-50 p-4"><p className="text-sm text-red-800">{error}</p></div>}
        {success && <div className="rounded-md bg-green-50 p-4"><p className="text-sm text-green-800">{success}</p></div>}

        {step === 'email' && (
          <form onSubmit={handleRequestCode} className="mt-8 space-y-6">
            <input
              type="email"
              required
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Correo electrónico"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Enviando...' : 'Enviar código'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleVerifyAndReset} className="mt-8 space-y-6">
            <input
              type="text"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Código de 6 dígitos"
              maxLength={6}
            />
            <button
              type="submit"
              className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Continuar
            </button>
          </form>
        )}

        {step === 'newPassword' && (
          <form onSubmit={handleVerifyAndReset} className="mt-8 space-y-6">
            <div>
              <input
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Nueva contraseña"
              />
              <div className="mt-2 text-xs text-gray-600">
                <p className="font-medium mb-1">La contraseña debe contener:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Mínimo 8 caracteres</li>
                  <li>Al menos una mayúscula</li>
                  <li>Al menos una minúscula</li>
                  <li>Al menos un número</li>
                  <li>Al menos un carácter especial (!@#$%^&*)</li>
                </ul>
              </div>
            </div>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Confirmar nueva contraseña"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Cambiando...' : 'Cambiar contraseña'}
            </button>
          </form>
        )}

        <div className="text-center">
          <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800">
            Volver a iniciar sesión
          </Link>
        </div>
      </div>
    </div>
  );
}
